import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';
import t from 'react-translate';
import { NvPillTab, BadgeColor } from 'shared/components/nv-pill-tab';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import { halfSpacing, standardSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import { semiBoldFontWeight, textMediumFontSize } from 'styles/global_defaults/fonts';
import { updateSavedView, deleteSavedView } from 'redux/actions/learner-progress';
import { LearnerProgressSavedView } from 'redux/schemas/models/learner-progress-saved-view';
import { useAppDispatch } from 'redux/store';
import useClickOutside from 'shared/hooks/use-click-outside';
import { NvDropdownOption } from 'shared/components/inputs/nv-dropdown';

const renameFormStyles = css`
  padding: ${standardSpacing}px;
  text-align: center;

  .rename-title {
    font-size: ${textMediumFontSize}px;
    font-weight: ${semiBoldFontWeight};
    margin-bottom: ${threeQuartersSpacing}px;
  }

  .rename-actions {
    gap: ${halfSpacing}px;
  }
`;

type SavedViewPillProps = {
  view: LearnerProgressSavedView;
  catalogId: string;
  isSelected: boolean;
  onClick: () => void;
  onViewUpdated: (updatedView: LearnerProgressSavedView) => void;
  onEditFilters: () => void;
  tabId: string;
};

const SavedViewPill = ({ view, catalogId, isSelected, onClick, onViewUpdated, onEditFilters, tabId }: SavedViewPillProps) => {
  const dispatch = useAppDispatch();
  const [isRenaming, setIsRenaming] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  useClickOutside(popoverContentRef, () => {
    if (isRenaming) cancelRename();
  }, [isRenaming]);

  useEffect(() => {
    if (isRenaming) {
      setTimeout(() => renameInputRef.current?.focus(), 0);
    }
  }, [isRenaming]);

  const openRename = () => {
    setIsRenaming(false);
    setTimeout(() => {
      setIsRenaming(true);
      setRenameValue(view.viewName);
    }, 0);
  };

  const cancelRename = () => {
    setIsRenaming(false);
    setRenameValue('');
  };

  const confirmRename = async () => {
    if (!renameValue.trim()) return;
    const result = await dispatch(updateSavedView({
      catalogId,
      id: view.id,
      viewName: renameValue.trim(),
    }));
    if (updateSavedView.fulfilled.match(result)) {
      onViewUpdated(result.payload);
    }
    cancelRename();
  };

  const menuItems: NvDropdownOption[] = [
    { type: 'text', text: t.LEARNER_PROGRESS.TABS.MENU_RENAME(), callback: openRename },
    {
      type: 'text',
      text: t.LEARNER_PROGRESS.TABS.MENU_EDIT_FILTERS(),
      callback: onEditFilters,
    },
    { type: 'divider' },
    {
      type: 'text',
      text: t.LEARNER_PROGRESS.TABS.MENU_DELETE(),
      class: 'text-danger',
      callback: () => {
        setIsConfirmingDelete(true);
      },
    },
  ];

  const renameContent = (
    <div css={renameFormStyles} ref={popoverContentRef}>
      <div className='rename-title'>
        {t.LEARNER_PROGRESS.TABS.RENAME_VIEW_TITLE()}
      </div>
      <NvTextInput
        ref={renameInputRef}
        value={renameValue}
        onChange={(e) => setRenameValue(e.target.value)}
        ariaLabel={t.LEARNER_PROGRESS.TABS.RENAME_VIEW_TITLE()}
        data-qa='rename-view-input'
      />
      <div className='rename-actions d-flex justify-content-center mt-3'>
        <Button
          size='sm'
          variant='outline-primary'
          onClick={cancelRename}
          data-qa='rename-view-cancel-button'
        >
          {t.FORM.CANCEL()}
        </Button>
        <Button
          size='sm'
          variant='primary'
          onClick={confirmRename}
          disabled={!renameValue.trim()}
          data-qa='rename-view-save-button'
        >
          {t.LEARNER_PROGRESS.SAVED_VIEWS.SAVE()}
        </Button>
      </div>
    </div>
  );

  return (
    <NvPillTab
      label={view.viewName}
      count={view.learnersCount ?? 0}
      isSelected={isSelected}
      onClick={onClick}
      id={tabId}
      badgeColor={BadgeColor.DANGER}
      menuItems={menuItems}
      confirmationPopover={{
        show: isConfirmingDelete,
        header: t.LEARNER_PROGRESS.TABS.DELETE_CONFIRM_HEADER(),
        cancelText: t.LEARNER_PROGRESS.TABS.DELETE_CONFIRM_CANCEL(),
        confirmText: t.LEARNER_PROGRESS.TABS.DELETE_CONFIRM_YES(),
        onCancel: () => setIsConfirmingDelete(false),
        onConfirm: () => {
          setIsConfirmingDelete(false);
          dispatch(deleteSavedView({ catalogId, id: view.id, viewName: view.viewName }));
        },
      }}
      popover={{
        show: isRenaming,
        content: renameContent,
      }}
      hoverContent={(
        <div className='hover-content'>
          <div className='mb-1'><strong>{view.viewName}:</strong></div>
          {view.filters.inactiveFor && <div>{t.LEARNER_PROGRESS.TABS.INACTIVE_HOVER_DESCRIPTION()} <strong>{t.LEARNER_PROGRESS.TABS.INACTIVE_HOVER_DAYS(String(view.filters.inactiveFor))}</strong></div>}
          {view.filters.assignmentIds && <div>{t.LEARNER_PROGRESS.FILTERS.HAS_NOT_COMPLETED()} <strong>{view.filters.assignmentIds}</strong></div>}
        </div>
      )}
    />
  );
};

export default SavedViewPill;
